import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { useGetUserQuery } from "apis/user";
import { useGetOrganizationsQuery } from "apis/organization";
import { useGetOfficesQuery } from "apis/office";
import { useGetLegalDocumentsQuery } from "apis/legalDocuments";
import { useLookupNswLicenseMutation } from "apis/company";
import Button from "components/ui/Button";
import {
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineLockClosed,
} from "react-icons/hi2";

// <input type="date"> only accepts YYYY-MM-DD.
const toDateInput = (value) =>
  value ? new Date(value).toISOString().slice(0, 10) : "";

// Default pricing values
const DEFAULT_MAIN_AGENT_PRICE = 699;
const DEFAULT_ADDONS = [
  {
    key: "socialMedia",
    name: "Social Media",
    unitPrice: 199,
    billingType: "perSeat",
    removable: true,
  },
  {
    key: "fruitBox",
    name: "Fruit Box",
    unitPrice: 60,
    billingType: "perProperty",
    removable: true,
  },
  {
    key: "campaignFee",
    name: "Campaign Fee",
    unitPrice: 599,
    billingType: "perProperty",
    removable: false,
  },
];

export default function CompanyEdit({ initialData = {}, onSubmit }) {
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedLicenseeInCharge, setSelectedLicenseeInCharge] =
    useState(null);
  const [selectedSecondaryOwners, setSelectedSecondaryOwners] = useState([]);
  const [selectedAdministrators, setSelectedAdministrators] = useState([]);
  const [selectedDefaultCoAgents, setSelectedDefaultCoAgents] = useState([]);
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [selectedRequiredDocuments, setSelectedRequiredDocuments] = useState(
    [],
  );

  // Enterprise pricing state
  const [isEnterprise, setIsEnterprise] = useState(
    initialData.isEnterprise || false,
  );
  const [mainAgentPrice, setMainAgentPrice] = useState(
    initialData.enterprisePricing?.mainAgent?.unitPrice ||
      DEFAULT_MAIN_AGENT_PRICE,
  );
  const [mainAgentName, setMainAgentName] = useState(
    initialData.enterprisePricing?.mainAgent?.name || "Main Agent",
  );
  const [addons, setAddons] = useState(
    initialData.enterprisePricing?.addons?.length > 0
      ? initialData.enterprisePricing.addons
      : DEFAULT_ADDONS,
  );

  // Get all users for owner selection
  const { data: users = [], isPending: usersLoading } = useGetUserQuery();

  // Get all organizations for organization selection
  const { data: organizations = [], isPending: organizationsLoading } =
    useGetOrganizationsQuery();

  // Get all offices for office selection
  const { data: offices = [], isPending: officesLoading } =
    useGetOfficesQuery();

  // Get all legal documents for required documents selection
  const { data: legalDocuments = [], isPending: legalDocsLoading } =
    useGetLegalDocumentsQuery();

  // NSW register lookup for the Corporate License block
  const { mutateAsync: lookupNswLicense, isPending: nswLoading } =
    useLookupNswLicenseMutation();
  const [nswCandidates, setNswCandidates] = useState(null);
  const [nswNotice, setNswNotice] = useState(null);

  // Helper function to get initial owner
  const getInitialOwner = () => {
    if (!initialData || !initialData.primaryOwner) return null;

    const ownerId = initialData.primaryOwner._id;
    const foundUser = users.find((user) => user._id === ownerId);

    if (foundUser) {
      return {
        value: foundUser._id,
        label: foundUser.name,
        user: foundUser,
      };
    }
    return null;
  };

  // Helper function to get initial organization
  const getInitialOrganization = () => {
    if (!initialData || !initialData.organizationId) return null;

    const orgId =
      typeof initialData.organizationId === "string"
        ? initialData.organizationId
        : initialData.organizationId._id;
    const foundOrg = organizations.find((org) => org._id === orgId);

    if (foundOrg) {
      return {
        value: foundOrg._id,
        label: foundOrg.name,
        organization: foundOrg,
      };
    }
    return null;
  };

  // Helper function to get initial licensee in charge
  const getInitialLicenseeInCharge = () => {
    if (!initialData || !initialData.licenseeInCharge) return null;

    const licId =
      typeof initialData.licenseeInCharge === "string"
        ? initialData.licenseeInCharge
        : initialData.licenseeInCharge._id;
    const foundUser = users.find((user) => user._id === licId);

    if (foundUser) {
      return {
        value: foundUser._id,
        label: foundUser.name,
        user: foundUser,
      };
    }
    return null;
  };

  // The co-owners or admins the API sends, as picker options.
  const userOptionsFor = (people) =>
    (Array.isArray(people) ? people : [])
      .map((person) => users.find((user) => user._id === person._id))
      .filter(Boolean)
      .map((user) => ({ value: user._id, label: user.name, user }));

  // Helper function to get initial default co-agents
  const getInitialDefaultCoAgents = () => {
    if (
      !initialData ||
      !initialData.defaultCoAgents ||
      !Array.isArray(initialData.defaultCoAgents)
    )
      return [];

    return initialData.defaultCoAgents
      .map((agent) => {
        const agentId = typeof agent === "string" ? agent : agent._id;
        const foundUser = users.find((user) => user._id === agentId);

        if (foundUser) {
          return {
            value: foundUser._id,
            label: foundUser.name,
            user: foundUser,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  // Helper function to get initial offices
  const getInitialOffices = () => {
    if (
      !initialData ||
      !initialData.offices ||
      !Array.isArray(initialData.offices)
    )
      return [];

    return initialData.offices
      .map((office) => {
        const officeId = typeof office === "string" ? office : office._id;
        const foundOffice = offices.find((off) => off._id === officeId);

        if (foundOffice) {
          return {
            value: foundOffice._id,
            label: `${foundOffice.name} - ${foundOffice.address}`,
            office: foundOffice,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  // Helper function to get initial required documents
  const getInitialRequiredDocuments = () => {
    if (
      !initialData ||
      !initialData.requiredDocuments ||
      !Array.isArray(initialData.requiredDocuments)
    )
      return [];

    return initialData.requiredDocuments
      .map((doc) => {
        const docId = typeof doc === "string" ? doc : doc._id;
        const foundDoc = legalDocuments.find((d) => d._id === docId);

        if (foundDoc) {
          return {
            value: foundDoc._id,
            label: foundDoc.name?.replace(/_/g, " "),
            document: foundDoc,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData.name || "",
      abn: initialData.abn || "",
      licenseNumber: initialData.licenseNumber || "",
      businessName: initialData.businessName || "",
      corporateLicense: {
        number: initialData.corporateLicense?.number || "",
        expiry: toDateInput(initialData.corporateLicense?.expiry),
        issuedTo: initialData.corporateLicense?.issuedTo || "",
        url: initialData.corporateLicense?.url || "",
        isValid: initialData.corporateLicense?.isValid || false,
        isExpired: initialData.corporateLicense?.isExpired || false,
      },
      reviewLink: initialData.reviewLink || "",
      primaryOwnerId: initialData.primaryOwner?._id || "",
      organizationId:
        initialData.organizationId?._id || initialData.organizationId || "",
      licenseeInCharge:
        initialData.licenseeInCharge?._id || initialData.licenseeInCharge || "",
      coOwnerIds: (initialData.coOwners || []).map((coOwner) => coOwner._id),
      administratorIds: (initialData.administrators || []).map(
        (administrator) => administrator._id,
      ),
      defaultCoAgents: initialData.defaultCoAgents || [],
      offices: initialData.offices || [],
      isEnterprise: initialData.isEnterprise || false,
      status: initialData.status || "active",
      requiredDocuments: initialData.requiredDocuments || [],
    },
  });

  // Initialize selected owner from initial data
  useEffect(() => {
    if (users.length > 0 && initialData.primaryOwner) {
      const initialOwner = getInitialOwner();
      if (initialOwner) {
        setSelectedOwner(initialOwner);
      }
    }
  }, [initialData.primaryOwner, users]);

  // Initialize selected organization from initial data
  useEffect(() => {
    if (organizations.length > 0 && initialData.organizationId) {
      const initialOrganization = getInitialOrganization();
      if (initialOrganization) {
        setSelectedOrganization(initialOrganization);
      }
    }
  }, [initialData.organizationId, organizations]);

  // Initialize selected licensee in charge from initial data
  useEffect(() => {
    if (users.length > 0 && initialData.licenseeInCharge) {
      const initialLic = getInitialLicenseeInCharge();
      if (initialLic) {
        setSelectedLicenseeInCharge(initialLic);
      }
    }
  }, [initialData.licenseeInCharge, users]);

  // Initialize selected co-owners and admins from initial data
  useEffect(() => {
    if (users.length === 0) return;
    const initialCoOwners = userOptionsFor(initialData.coOwners);
    if (initialCoOwners.length > 0) setSelectedSecondaryOwners(initialCoOwners);
    const initialAdministrators = userOptionsFor(initialData.administrators);
    if (initialAdministrators.length > 0) {
      setSelectedAdministrators(initialAdministrators);
    }
  }, [initialData.coOwners, initialData.administrators, users]);

  // Initialize selected default co-agents from initial data
  useEffect(() => {
    if (users.length > 0 && initialData.defaultCoAgents) {
      const initialDefaultCoAgents = getInitialDefaultCoAgents();
      if (initialDefaultCoAgents.length > 0) {
        setSelectedDefaultCoAgents(initialDefaultCoAgents);
      }
    }
  }, [initialData.defaultCoAgents, users]);

  // Initialize selected offices from initial data
  useEffect(() => {
    if (offices.length > 0 && initialData.offices) {
      const initialOffices = getInitialOffices();
      if (initialOffices.length > 0) {
        setSelectedOffices(initialOffices);
      }
    }
  }, [initialData.offices, offices]);

  // Initialize selected required documents from initial data
  useEffect(() => {
    if (legalDocuments.length > 0 && initialData.requiredDocuments) {
      const initialDocs = getInitialRequiredDocuments();
      if (initialDocs.length > 0) {
        setSelectedRequiredDocuments(initialDocs);
      }
    }
  }, [initialData.requiredDocuments, legalDocuments]);

  // Addon handlers
  const handleAddonChange = (index, field, value) => {
    const updated = [...addons];
    updated[index] = { ...updated[index], [field]: value };
    setAddons(updated);

    // Clear error for this field
    if (addonErrors[index]?.[field]) {
      const newErrors = { ...addonErrors };
      delete newErrors[index][field];
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index];
      }
      setAddonErrors(newErrors);
    }
  };

  const handleAddAddon = () => {
    setAddons([
      ...addons,
      { key: "", name: "", unitPrice: 0, billingType: "perSeat" },
    ]);
  };

  const handleRemoveAddon = (index) => {
    setAddons(addons.filter((_, i) => i !== index));
  };

  // Addon validation errors
  const [addonErrors, setAddonErrors] = useState({});

  // Validate addons before submit
  const validateAddons = () => {
    const errors = {};
    let isValid = true;

    addons.forEach((addon, index) => {
      const addonErr = {};

      if (!addon.key || addon.key.trim() === "") {
        addonErr.key = "Key is required";
        isValid = false;
      } else if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(addon.key)) {
        addonErr.key = "Key must start with letter, no spaces";
        isValid = false;
      }

      if (!addon.name || addon.name.trim() === "") {
        addonErr.name = "Name is required";
        isValid = false;
      }

      if (!addon.unitPrice || Number(addon.unitPrice) <= 0) {
        addonErr.unitPrice = "Price must be greater than 0";
        isValid = false;
      }

      if (Object.keys(addonErr).length > 0) {
        errors[index] = addonErr;
      }
    });

    // Check for duplicate keys
    const keys = addons.map((a) => a.key?.toLowerCase()).filter(Boolean);
    const duplicates = keys.filter((key, idx) => keys.indexOf(key) !== idx);
    if (duplicates.length > 0) {
      addons.forEach((addon, index) => {
        if (duplicates.includes(addon.key?.toLowerCase())) {
          errors[index] = { ...errors[index], key: "Duplicate key" };
          isValid = false;
        }
      });
    }

    setAddonErrors(errors);
    return isValid;
  };

  const submitForm = (data) => {
    // Validate addons if enterprise
    if (isEnterprise && !validateAddons()) {
      return;
    }

    const transformedData = {
      ...data,
      primaryOwnerId: selectedOwner?.value || "",
      organizationId: selectedOrganization?.value || "",
      licenseeInCharge: selectedLicenseeInCharge?.value || "",
      coOwnerIds: selectedSecondaryOwners.map((owner) => owner.value),
      administratorIds: selectedAdministrators.map((admin) => admin.value),
      defaultCoAgents: selectedDefaultCoAgents.map((agent) => agent.value),
      offices: selectedOffices.map((office) => office.value),
      requiredDocuments: selectedRequiredDocuments.map((doc) => doc.value),
      isEnterprise,
    };

    // Add enterprise pricing if enterprise
    if (isEnterprise) {
      transformedData.enterprisePricing = {
        mainAgent: {
          unitPrice: Number(mainAgentPrice),
          name: mainAgentName,
        },
        addons: addons.map((addon) => ({
          key: addon.key,
          name: addon.name,
          unitPrice: Number(addon.unitPrice),
          billingType: addon.billingType,
          removable: addon.removable !== false,
          enabled: true,
        })),
      };
    }

    onSubmit(transformedData);
  };

  const handleOwnerChange = (selectedOption) => {
    setSelectedOwner(selectedOption);
    setValue("primaryOwnerId", selectedOption?.value || "");
    // The new primary owner can't also sit in one of the lists below.
    const notOwner = (option) => option.value !== selectedOption?.value;
    handleSecondaryOwnersChange(selectedSecondaryOwners.filter(notOwner));
    handleAdministratorsChange(selectedAdministrators.filter(notOwner));
  };

  const handleOrganizationChange = (selectedOption) => {
    setSelectedOrganization(selectedOption);
    setValue("organizationId", selectedOption?.value || "");
  };

  const handleLicenseeInChargeChange = (selectedOption) => {
    setSelectedLicenseeInCharge(selectedOption);
    setValue("licenseeInCharge", selectedOption?.value || "");
  };

  // Write a resolved register record into the form. Business name and ABN are
  // only filled when empty — a lookup must not overwrite what was typed by
  // hand. The licence fields themselves are the point of the lookup, so those
  // always take the register's values.
  const applyNswLicense = (data) => {
    // Never overwrite a business name that's already there. The register is
    // often missing one, and where it has one it can differ from the trading
    // name we print.
    const typedBusinessName = getValues("businessName")?.trim();
    if (data.businessName && !typedBusinessName) {
      setValue("businessName", data.businessName);
    }

    setValue("corporateLicense.number", data.corporateLicense.number || "");
    setValue(
      "corporateLicense.expiry",
      toDateInput(data.corporateLicense.expiry),
    );
    setValue("corporateLicense.issuedTo", data.corporateLicense.issuedTo || "");
    setValue("corporateLicense.isValid", data.corporateLicense.isValid);
    setValue("corporateLicense.isExpired", data.corporateLicense.isExpired);
    if (data.abn && !getValues("abn")) setValue("abn", data.abn);

    const notes = [];
    if (data.resolvedFromIndividual) {
      notes.push(
        `${data.resolvedFromIndividual.number} is ${data.resolvedFromIndividual.name}'s personal licence — used it to find the corporation.`,
      );
    }
    if (data.licenseeInChargeName) {
      notes.push(`Register lists ${data.licenseeInChargeName} as licensee in charge.`);
    }
    if (!data.businessName) {
      notes.push(
        typedBusinessName
          ? "No business name on the register — kept yours."
          : "No business name registered against this licence.",
      );
    } else if (typedBusinessName && typedBusinessName !== data.businessName) {
      notes.push(
        `Register has business name "${data.businessName}" — kept yours.`,
      );
    }
    setNswNotice(notes.join(" "));
    setNswCandidates(null);
  };

  // The button: try the licence number we already hold, else fall back to a
  // name search. The register matches a legal entity name far better than a
  // trading name, so name hits are shown as candidates rather than applied.
  const handleNswLookup = async () => {
    setNswNotice(null);
    setNswCandidates(null);

    const licenceNumber =
      getValues("corporateLicense.number")?.trim() ||
      getValues("licenseNumber")?.trim();
    const searchText = getValues("name")?.trim();

    try {
      if (licenceNumber) {
        const res = await lookupNswLicense({ licenceNumber });
        if (res?.data?.mode === "resolved") return applyNswLicense(res.data.data);
      }

      if (!searchText) {
        setNswNotice("Enter a licence number or company name first.");
        return;
      }

      const res = await lookupNswLicense({ searchText });
      const candidates = res?.data?.data || [];
      setNswCandidates(candidates);
      if (!candidates.length) {
        setNswNotice(
          `No corporation licence found for "${searchText}". Try the legal entity name — the register often has no trading name against the licence.`,
        );
      }
    } catch {
      // The mutation's onError already surfaced a toast.
    }
  };

  const handleNswCandidate = async (licenceNumber) => {
    try {
      const res = await lookupNswLicense({ licenceNumber });
      if (res?.data?.mode === "resolved") applyNswLicense(res.data.data);
    } catch {
      // handled by the mutation's onError
    }
  };

  const handleSecondaryOwnersChange = (selectedOptions) => {
    const ownersArray = selectedOptions || [];
    setSelectedSecondaryOwners(ownersArray);
    setValue(
      "coOwnerIds",
      ownersArray.map((owner) => owner.value),
    );
  };

  const handleAdministratorsChange = (selectedOptions) => {
    const administratorsArray = selectedOptions || [];
    setSelectedAdministrators(administratorsArray);
    setValue(
      "administratorIds",
      administratorsArray.map((admin) => admin.value),
    );
  };

  const handleDefaultCoAgentsChange = (selectedOptions) => {
    const agentsArray = selectedOptions || [];
    setSelectedDefaultCoAgents(agentsArray);
    setValue(
      "defaultCoAgents",
      agentsArray.map((agent) => agent.value),
    );
  };

  const handleOfficesChange = (selectedOptions) => {
    const officesArray = selectedOptions || [];
    setSelectedOffices(officesArray);
    setValue(
      "offices",
      officesArray.map((office) => office.value),
    );
  };

  const handleRequiredDocumentsChange = (selectedOptions) => {
    const docsArray = selectedOptions || [];
    setSelectedRequiredDocuments(docsArray);
    setValue(
      "requiredDocuments",
      docsArray.map((doc) => doc.value),
    );
  };

  // User options for owner selection
  const userOptions = users.map((user) => ({
    value: user._id,
    label: user.name,
    user,
  }));

  // Organization options for organization selection
  const organizationOptions = organizations.map((org) => ({
    value: org._id,
    label: org.name,
    organization: org,
  }));

  // One person holds one role at a company, so each list leaves out the
  // primary owner and whoever the other list already has.
  const optionsExcluding = (taken) =>
    users
      .filter(
        (user) =>
          user._id !== selectedOwner?.value &&
          !taken.some((option) => option.value === user._id),
      )
      .map((user) => ({ value: user._id, label: user.name, user }));
  const secondaryOwnerOptions = optionsExcluding(selectedAdministrators);
  const administratorOptions = optionsExcluding(selectedSecondaryOwners);

  // Any user can be a default co-agent — a concierge usually sits outside the
  // owner/secondary-owner set.
  const defaultCoAgentOptions = users.map((user) => ({
    value: user._id,
    label: user.name,
    user,
  }));

  // Office options for office selection
  const officeOptions = offices.map((office) => ({
    value: office._id,
    label: `${office.name} - ${office.address}`,
    office,
  }));

  // Legal document options for required documents selection
  const legalDocumentOptions = legalDocuments.map((doc) => ({
    value: doc._id,
    label: doc.name?.replace(/_/g, " "),
    document: doc,
  }));

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "active", label: "Active" },
    { value: "suspended", label: "Suspended" },
    { value: "inactive", label: "Inactive" },
  ];

  const billingTypeOptions = [
    { value: "perSeat", label: "Per Seat (monthly)" },
    { value: "perProperty", label: "Per Property (one-time)" },
    { value: "flat", label: "Flat Rate (monthly)" },
  ];

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-4">
      {/* Company Name */}
      <div className="space-y-2">
        <label htmlFor="name" className="form-label">
          Company Name *
        </label>
        <input
          type="text"
          id="name"
          {...register("name", { required: "Company name is required" })}
          placeholder="e.g., ABC Real Estate Pty Ltd"
          className={`form-input border ${
            errors.name ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.name && (
          <p className="form-error-message">{errors.name.message}</p>
        )}
      </div>

      {/* Organization Selection */}
      <div className="space-y-2">
        <label htmlFor="organizationId" className="form-label">
          Organization
        </label>
        <Controller
          name="organizationId"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={selectedOrganization}
              onChange={(selectedOption) => {
                handleOrganizationChange(selectedOption);
                field.onChange(selectedOption?.value || "");
              }}
              options={organizationOptions}
              placeholder="Select organization"
              className="basic-single"
              classNamePrefix="select"
              isClearable
              isLoading={organizationsLoading}
              isSearchable
            />
          )}
        />
        <p className="text-sm text-darkgray">
          The organization this company belongs to
        </p>
      </div>

      {/* Owner Selection */}
      <div className="space-y-2">
        <label htmlFor="primaryOwnerId" className="form-label">
          Company Owner *
        </label>
        <Controller
          name="primaryOwnerId"
          control={control}
          rules={{ required: "Company owner is required" }}
          render={({ field }) => (
            <Select
              {...field}
              value={selectedOwner}
              onChange={(selectedOption) => {
                handleOwnerChange(selectedOption);
                field.onChange(selectedOption?.value || "");
              }}
              options={userOptions}
              placeholder="Select company owner"
              className="basic-single"
              classNamePrefix="select"
              isClearable
              isLoading={usersLoading}
              isSearchable
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: errors.primaryOwnerId
                    ? "#ef4444"
                    : baseStyles.borderColor,
                }),
              }}
            />
          )}
        />
        {errors.primaryOwnerId && (
          <p className="form-error-message">{errors.primaryOwnerId.message}</p>
        )}
      </div>

      {/* Licensee in Charge Selection */}
      <div className="space-y-2">
        <label htmlFor="licenseeInCharge" className="form-label">
          Licensee in Charge
        </label>
        <Controller
          name="licenseeInCharge"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={selectedLicenseeInCharge}
              onChange={(selectedOption) => {
                handleLicenseeInChargeChange(selectedOption);
                field.onChange(selectedOption?.value || "");
              }}
              options={userOptions}
              placeholder="Select licensee in charge"
              className="basic-single"
              classNamePrefix="select"
              isClearable
              isLoading={usersLoading}
              isSearchable
            />
          )}
        />
        <p className="text-sm text-darkgray">
          The licensed person responsible for supervising this company
        </p>
      </div>

      {/* Secondary Owners Selection */}
      <div className="space-y-2">
        <label htmlFor="coOwnerIds" className="form-label">
          Secondary Owners
        </label>
        <Controller
          name="coOwnerIds"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={selectedSecondaryOwners}
              onChange={(selectedOptions) => {
                handleSecondaryOwnersChange(selectedOptions);
                field.onChange(selectedOptions?.map((o) => o.value) || []);
              }}
              options={secondaryOwnerOptions}
              placeholder="Select secondary owners"
              className="basic-multi-select"
              classNamePrefix="select"
              isClearable
              isLoading={usersLoading}
              isSearchable
              isMulti
            />
          )}
        />
        <p className="text-sm text-darkgray">
          Other directors who own this company alongside the owner
        </p>
      </div>

      {/* Company Admins Selection */}
      <div className="space-y-2">
        <label htmlFor="administratorIds" className="form-label">
          Company Admins
        </label>
        <Controller
          name="administratorIds"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={selectedAdministrators}
              onChange={(selectedOptions) => {
                handleAdministratorsChange(selectedOptions);
                field.onChange(selectedOptions?.map((o) => o.value) || []);
              }}
              options={administratorOptions}
              placeholder="Select company admins"
              className="basic-multi-select"
              classNamePrefix="select"
              isClearable
              isLoading={usersLoading}
              isSearchable
              isMulti
            />
          )}
        />
        <p className="text-sm text-darkgray">
          People who manage this company for its owners, without an ownership
          stake
        </p>
      </div>

      {/* Default Co-Agents Selection */}
      <div className="space-y-2">
        <label htmlFor="defaultCoAgents" className="form-label">
          Default Co-Agents
        </label>
        <Controller
          name="defaultCoAgents"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={selectedDefaultCoAgents}
              onChange={(selectedOptions) => {
                handleDefaultCoAgentsChange(selectedOptions);
                field.onChange(selectedOptions?.map((o) => o.value) || []);
              }}
              options={defaultCoAgentOptions}
              placeholder="Select default co-agents"
              className="basic-multi-select"
              classNamePrefix="select"
              isClearable
              isLoading={usersLoading}
              isSearchable
              isMulti
            />
          )}
        />
        <p className="text-sm text-darkgray">
          Added as followers on every new auth schedule for this company (e.g. a
          concierge). Applied when the agreement is created — agents can still
          remove them afterwards.
        </p>
      </div>

      {/* Enterprise & Status Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Is Enterprise */}
        <div className="space-y-2">
          <label className="form-label">Company Type</label>
          <div className="flex items-start gap-3 p-3 border border-mediumgray rounded-md">
            <input
              type="checkbox"
              id="isEnterprise"
              checked={isEnterprise}
              onChange={(e) => setIsEnterprise(e.target.checked)}
              className="w-4 h-4 mt-0.5"
            />
            <label htmlFor="isEnterprise" className="cursor-pointer">
              <span className="font-medium">Enterprise Company</span>
              <p className="text-sm text-darkgray">
                Owner pays for all agents under this company
              </p>
            </label>
          </div>
        </div>

        {/* Status */}
        <div className="space-y-2">
          <label htmlFor="status" className="form-label">
            Status
          </label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={statusOptions.find((opt) => opt.value === field.value)}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption?.value || "active");
                }}
                options={statusOptions}
                placeholder="Select status"
                className="basic-single"
                classNamePrefix="select"
              />
            )}
          />
        </div>
      </div>

      {/* Enterprise Pricing Section - Only show when isEnterprise is true */}
      {isEnterprise && (
        <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="font-medium text-blue-800">Enterprise Pricing</div>

          {/* Main Agent Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="form-label">Seat Name</label>
              <input
                type="text"
                value={mainAgentName}
                onChange={(e) => setMainAgentName(e.target.value)}
                placeholder="e.g., Main Agent"
                className="form-input border border-mediumgray"
              />
            </div>
            <div className="space-y-2">
              <label className="form-label">Price per Seat ($/month)</label>
              <input
                type="number"
                value={mainAgentPrice}
                onChange={(e) => setMainAgentPrice(e.target.value)}
                placeholder="699"
                className="form-input border border-mediumgray"
              />
            </div>
          </div>

          {/* Addons */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="form-label mb-0">Addons</label>
              <button
                type="button"
                onClick={handleAddAddon}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <HiOutlinePlus className="w-4 h-4" />
                Add Addon
              </button>
            </div>

            {addons.map((addon, index) => (
              <div
                key={index}
                className={`p-3 border rounded-lg space-y-3 ${
                  addon.removable === false
                    ? "bg-purple-50 border-purple-200"
                    : "bg-white border-mediumgray"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      Addon {index + 1}
                    </span>
                    {addon.removable === false && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-200 text-purple-700 text-xs rounded-full">
                        <HiOutlineLockClosed className="w-3 h-3" />
                        Required
                      </span>
                    )}
                  </div>
                  {addon.removable !== false && (
                    <button
                      type="button"
                      onClick={() => handleRemoveAddon(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <HiOutlineTrash className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-darkgray">
                      Key (unique identifier)
                    </label>
                    <input
                      type="text"
                      value={addon.key}
                      onChange={(e) =>
                        handleAddonChange(index, "key", e.target.value)
                      }
                      placeholder="e.g., socialMedia"
                      className={`form-input border text-sm ${
                        addonErrors[index]?.key
                          ? "border-red-500"
                          : "border-mediumgray"
                      }`}
                    />
                    {addonErrors[index]?.key && (
                      <p className="text-xs text-red-500">
                        {addonErrors[index].key}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-darkgray">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={addon.name}
                      onChange={(e) =>
                        handleAddonChange(index, "name", e.target.value)
                      }
                      placeholder="e.g., Social Media"
                      className={`form-input border text-sm ${
                        addonErrors[index]?.name
                          ? "border-red-500"
                          : "border-mediumgray"
                      }`}
                    />
                    {addonErrors[index]?.name && (
                      <p className="text-xs text-red-500">
                        {addonErrors[index].name}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-darkgray">Price ($)</label>
                    <input
                      type="number"
                      value={addon.unitPrice}
                      onChange={(e) =>
                        handleAddonChange(index, "unitPrice", e.target.value)
                      }
                      placeholder="199"
                      className={`form-input border text-sm ${
                        addonErrors[index]?.unitPrice
                          ? "border-red-500"
                          : "border-mediumgray"
                      }`}
                    />
                    {addonErrors[index]?.unitPrice && (
                      <p className="text-xs text-red-500">
                        {addonErrors[index].unitPrice}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-darkgray">
                      Billing Type
                    </label>
                    <select
                      value={addon.billingType}
                      onChange={(e) =>
                        handleAddonChange(index, "billingType", e.target.value)
                      }
                      className="form-input border border-mediumgray text-sm"
                    >
                      {billingTypeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}

            {addons.length === 0 && (
              <p className="text-sm text-darkgray text-center py-4">
                No addons configured. Click "Add Addon" to create one.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Required Documents Selection */}
      <div className="space-y-2">
        <label htmlFor="requiredDocuments" className="form-label">
          Required Documents to Sign
        </label>
        <Controller
          name="requiredDocuments"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={selectedRequiredDocuments}
              onChange={(selectedOptions) => {
                handleRequiredDocumentsChange(selectedOptions);
                field.onChange(selectedOptions?.map((d) => d.value) || []);
              }}
              options={legalDocumentOptions}
              placeholder="Select documents this company must sign"
              className="basic-multi-select"
              classNamePrefix="select"
              isClearable
              isLoading={legalDocsLoading}
              isSearchable
              isMulti
            />
          )}
        />
        <p className="text-sm text-darkgray">
          Select which legal documents this company is required to sign
        </p>
      </div>

      {/* Offices Selection */}
      <div className="space-y-2">
        <label htmlFor="offices" className="form-label">
          Company Offices
        </label>
        <Controller
          name="offices"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={selectedOffices}
              onChange={(selectedOptions) => {
                handleOfficesChange(selectedOptions);
                field.onChange(
                  selectedOptions?.map((office) => office.value) || [],
                );
              }}
              options={officeOptions}
              placeholder="Select company offices"
              className="basic-multi-select"
              classNamePrefix="select"
              isClearable
              isLoading={officesLoading}
              isSearchable
              isMulti
            />
          )}
        />
        <p className="text-sm text-darkgray">
          Select multiple offices that belong to this company
        </p>
      </div>

      {/* ABN */}
      <div className="space-y-2">
        <label htmlFor="abn" className="form-label">
          ABN *
        </label>
        <input
          type="text"
          id="abn"
          {...register("abn", {
            required: "ABN is required",
            pattern: {
              value: /^\d{11}$/,
              message: "ABN must be 11 digits",
            },
          })}
          placeholder="e.g., 12345678901"
          className={`form-input border ${
            errors.abn ? "border-red-500" : "border-mediumgray"
          }`}
          maxLength={11}
        />
        {errors.abn && (
          <p className="form-error-message">{errors.abn.message}</p>
        )}
      </div>

      {/* License Number */}
      <div className="space-y-2">
        <label htmlFor="licenseNumber" className="form-label">
          License Number *
        </label>
        <input
          type="text"
          id="licenseNumber"
          {...register("licenseNumber", {
            required: "License number is required",
          })}
          placeholder="e.g., 10044297"
          className={`form-input border ${
            errors.licenseNumber ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.licenseNumber && (
          <p className="form-error-message">{errors.licenseNumber.message}</p>
        )}
      </div>

      {/* Business Name */}
      <div className="space-y-2">
        <label htmlFor="businessName" className="form-label">
          Business Name
        </label>
        <input
          type="text"
          id="businessName"
          {...register("businessName")}
          placeholder="Trading name, if it differs from the company name"
          className="form-input border border-mediumgray"
        />
        <p className="text-sm text-darkgray">
          Printed as the LICENSEE business name on agreements when a licensee in
          charge is set. Falls back to the company name.
        </p>
      </div>

      {/* Corporate License */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-4">
          <label className="form-label">Corporate License</label>
          <Button
            type="button"
            onClick={handleNswLookup}
            loading={nswLoading}
            className="black-button whitespace-nowrap"
          >
            Look up NSW register
          </Button>
        </div>

        {nswNotice && (
          <p className="rounded bg-lightgray p-2 text-sm text-darkgray">
            {nswNotice}
          </p>
        )}

        {nswCandidates?.length > 0 && (
          <div className="space-y-2 rounded border border-mediumgray p-3">
            <p className="text-sm text-darkgray">
              Pick the matching corporation licence:
            </p>
            {nswCandidates.map((candidate) => (
              <button
                key={candidate.licenceNumber}
                type="button"
                onClick={() => handleNswCandidate(candidate.licenceNumber)}
                className="block w-full rounded border border-mediumgray p-2 text-left text-sm hover:bg-lightgray"
              >
                <span className="font-medium">{candidate.licensee}</span>
                <span className="text-darkgray">
                  {" "}
                  · {candidate.licenceNumber} · {candidate.status} · expires{" "}
                  {candidate.expiryDate}
                </span>
                {candidate.businessNames && (
                  <span className="block text-darkgray">
                    trading as {candidate.businessNames}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label
              htmlFor="corporateLicense.number"
              className="text-sm text-darkgray"
            >
              Number
            </label>
            <input
              type="text"
              id="corporateLicense.number"
              {...register("corporateLicense.number")}
              placeholder="e.g., 10044297"
              className="form-input border border-mediumgray"
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="corporateLicense.expiry"
              className="text-sm text-darkgray"
            >
              Expiry
            </label>
            <input
              type="date"
              id="corporateLicense.expiry"
              {...register("corporateLicense.expiry")}
              className="form-input border border-mediumgray"
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="corporateLicense.issuedTo"
              className="text-sm text-darkgray"
            >
              Issued To
            </label>
            <input
              type="text"
              id="corporateLicense.issuedTo"
              {...register("corporateLicense.issuedTo")}
              placeholder="Name on the licence"
              className="form-input border border-mediumgray"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <label
              htmlFor="corporateLicense.url"
              className="text-sm text-darkgray"
            >
              License URL
            </label>
            <input
              type="url"
              id="corporateLicense.url"
              {...register("corporateLicense.url")}
              placeholder="https://..."
              className="form-input border border-mediumgray"
            />
          </div>
        </div>

        <div className="flex gap-6 pt-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              {...register("corporateLicense.isValid")}
              className="h-4 w-4"
            />
            Valid
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              {...register("corporateLicense.isExpired")}
              className="h-4 w-4"
            />
            Expired
          </label>
        </div>
        <p className="text-sm text-darkgray">
          Used as the LICENSEE licence number on agreements when a licensee in
          charge is set. Falls back to the company license number.
        </p>
      </div>

      {/* Review Link */}
      <div className="space-y-2">
        <label htmlFor="reviewLink" className="form-label">
          Review Link
        </label>
        <input
          type="url"
          id="reviewLink"
          {...register("reviewLink", {
            pattern: {
              value:
                /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
              message: "Please enter a valid URL",
            },
          })}
          placeholder="e.g., https://g.page/r/your-review-link"
          className={`form-input border ${
            errors.reviewLink ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.reviewLink && (
          <p className="form-error-message">{errors.reviewLink.message}</p>
        )}
        <p className="text-sm text-darkgray">
          Add a Google Reviews or other review platform link for your company
        </p>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end mt-6">
        <Button type="submit" className="black-button">
          {initialData._id ? "Update Company" : "Create Company"}
        </Button>
      </div>
    </form>
  );
}
